/**
 * The guide overlay.
 *
 * Every concept the tool asks you to have an opinion about is explained here,
 * and every `?` marker in the app links into it. It lives inside the bundle
 * rather than behind a link because the whole application is offline-first —
 * documentation you can only read online is documentation half the users never
 * have.
 */

import { useEffect, useRef } from 'react';
import { GUIDE_SECTIONS, TOPICS, type TopicId } from './glossary.tsx';

interface Props {
  /** Section or topic to scroll to on open. */
  focus?: TopicId;
  onClose: () => void;
}

const anchorId = (topic: TopicId) => `guide-${topic}`;

export function Guide({ focus, onClose }: Props) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Scroll within the overlay rather than calling scrollIntoView, which would
  // also scroll the page behind it.
  useEffect(() => {
    if (!focus) return;
    const el = bodyRef.current?.querySelector<HTMLElement>(`#${CSS.escape(anchorId(focus))}`);
    const container = bodyRef.current;
    if (el && container) container.scrollTop = el.offsetTop - 16;
  }, [focus]);

  return (
    <div className="guide-overlay" role="dialog" aria-modal="true" aria-label="Guide">
      <div className="guide">
        <header className="guide-head">
          <h2>Guide</h2>
          <p>
            What every field means, and what goes wrong if you get it wrong. Nothing here leaves
            your browser.
          </p>
          <button type="button" className="guide-close" ref={closeRef} onClick={onClose}>
            Close
          </button>
        </header>

        <div className="guide-body" ref={bodyRef}>
          <nav className="guide-nav" aria-label="Guide contents">
            {GUIDE_SECTIONS.map((s) => (
              <div key={s.id}>
                <h4>{s.title}</h4>
                <ul>
                  {s.topics.map((id) => (
                    <li key={id}>
                      <a href={`#${anchorId(id)}`}>{TOPICS[id].term}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <div className="guide-content">
            <section className="guide-intro">
              <h3>What this tool does</h3>
              <p>
                You have a design whose behaviour depends on parts that are never exactly nominal.
                Tolerance analysis asks the resulting question: given how much each part actually
                varies, how much does the thing you care about vary — and how many units will fall
                outside spec?
              </p>
              <p>
                Describe the variation as distributions, describe the relationship as an
                expression, and the tool answers that three ways at once: a{' '}
                <strong>worst-case</strong> guarantee, an analytic <strong>RSS</strong> estimate,
                and a <strong>Monte Carlo</strong> simulation. Then it ranks which tolerance is
                worth tightening.
              </p>
              <p>
                Everything runs in this browser. There is no account, no upload, and no network
                traffic after the page loads — the content security policy forbids external
                origins outright, so that is enforced rather than promised.
              </p>
            </section>

            {GUIDE_SECTIONS.map((s) => (
              <section key={s.id} className="guide-section">
                <h3 id={`guide-section-${s.id}`}>{s.title}</h3>
                <div className="guide-section-intro">{s.intro}</div>

                {s.topics.map((id) => {
                  const t = TOPICS[id];
                  return (
                    <article key={id} id={anchorId(id)} className="guide-topic">
                      <h4>{t.term}</h4>
                      {t.body}
                    </article>
                  );
                })}
              </section>
            ))}

            <section className="guide-section">
              <h3>Getting started</h3>
              <ol className="guide-steps">
                <li>
                  Open an <strong>example</strong> from the Open menu and run it. Three worked
                  projects are included; reading a complete model is faster than building one.
                </li>
                <li>
                  Change one tolerance and run again. Watch the histogram, the yield, and the
                  ranking move.
                </li>
                <li>
                  Build your own: add variables, give each a distribution, write the transfer
                  function, set the limits.
                </li>
                <li>
                  Check the <strong>method comparison</strong>. If RSS and Monte Carlo disagree,
                  your system is not linear over these tolerances and Monte Carlo is the answer.
                </li>
                <li>
                  Read the <strong>tolerance ranking</strong> last. It tells you what to change,
                  which is the point of the exercise.
                </li>
              </ol>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
